'use client';

import {
  ArrowDownLeft,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Check,
  ChevronRight,
  RefreshCw,
  Sparkles,
  User,
  X,
  Zap,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

// Types
interface FieldChange {
  id: string;
  fieldName: string;
  fieldLabel: string;
  currentValue: string | null;
  newValue: string | null;
  selected: boolean;
}

interface SyncSuggestion {
  id: string;
  entityName: string;
  entityType: 'provider' | 'organization' | 'contact';
  direction: 'push' | 'pull';
  matchConfidence: number;
  aiReasoning: string;
  timestamp: Date;
  changes: FieldChange[];
  status: 'pending' | 'accepted' | 'rejected';
}

// Mock data
const mockSuggestions: SyncSuggestion[] = [
  {
    id: '1',
    entityName: 'Dr. Sarah Smith',
    entityType: 'provider',
    direction: 'push',
    matchConfidence: 98,
    aiReasoning: 'Finch has newer contact information',
    timestamp: new Date(Date.now() - 2 * 60 * 1000),
    changes: [
      {
        id: '1a',
        fieldName: 'phone',
        fieldLabel: 'Phone',
        currentValue: '(555) 123-4567',
        newValue: '(555) 987-6543',
        selected: true,
      },
      {
        id: '1b',
        fieldName: 'fax',
        fieldLabel: 'Fax',
        currentValue: null,
        newValue: '(555) 987-6500',
        selected: true,
      },
    ],
    status: 'pending',
  },
  {
    id: '2',
    entityName: 'Wellstar Cobb Medical Center',
    entityType: 'organization',
    direction: 'pull',
    matchConfidence: 94,
    aiReasoning: 'Filevine has updated billing address',
    timestamp: new Date(Date.now() - 15 * 60 * 1000),
    changes: [
      {
        id: '2a',
        fieldName: 'billing_address',
        fieldLabel: 'Billing Address',
        currentValue: '123 Medical Dr, Suite 100',
        newValue: '456 Healthcare Blvd, Floor 3',
        selected: true,
      },
      {
        id: '2b',
        fieldName: 'department_phone',
        fieldLabel: 'Department Phone',
        currentValue: '(404) 555-0100',
        newValue: '(404) 555-0200',
        selected: true,
      },
      {
        id: '2c',
        fieldName: 'department_email',
        fieldLabel: 'Department Email',
        currentValue: 'records@wellstar.org',
        newValue: 'billing@wellstar.org',
        selected: false,
      },
    ],
    status: 'pending',
  },
  {
    id: '3',
    entityName: 'Metro Physical Therapy',
    entityType: 'organization',
    direction: 'push',
    matchConfidence: 87,
    aiReasoning: 'Contact person changed in Finch',
    timestamp: new Date(Date.now() - 45 * 60 * 1000),
    changes: [
      {
        id: '3a',
        fieldName: 'primary_contact',
        fieldLabel: 'Contact',
        currentValue: 'John Doe',
        newValue: 'Jane Williams',
        selected: true,
      },
    ],
    status: 'pending',
  },
  {
    id: '4',
    entityName: 'Dr. Michael Chen',
    entityType: 'provider',
    direction: 'pull',
    matchConfidence: 76,
    aiReasoning: 'NPI data more complete in Filevine',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    changes: [
      {
        id: '4a',
        fieldName: 'npi',
        fieldLabel: 'NPI',
        currentValue: null,
        newValue: '1234567890',
        selected: true,
      },
      {
        id: '4b',
        fieldName: 'specialty',
        fieldLabel: 'Specialty',
        currentValue: 'General Practice',
        newValue: 'Orthopedic Surgery',
        selected: true,
      },
    ],
    status: 'pending',
  },
];

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return date.toLocaleDateString();
}

// Compact card component
const CompactSyncCard = ({
  suggestion,
  onAccept,
  onReject,
  onExpand,
  index,
}: {
  suggestion: SyncSuggestion;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onExpand: (id: string) => void;
  index: number;
}) => {
  const isPush = suggestion.direction === 'push';
  const EntityIcon = suggestion.entityType === 'provider' ? User : Building2;
  const changeCount = suggestion.changes.length;
  const selectedCount = suggestion.changes.filter((c) => c.selected).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      className="group"
    >
      <div className="relative bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 overflow-hidden">
        <div className="flex items-stretch">
          {/* Direction indicator */}
          <div
            className={`w-1.5 ${isPush ? 'bg-blue-500' : 'bg-emerald-500'}`}
          />

          {/* Main content */}
          <div className="flex-1 p-4 flex items-center gap-4">
            {/* Entity icon & name */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className={`p-2 rounded-lg ${isPush ? 'bg-blue-50' : 'bg-emerald-50'}`}
              >
                <EntityIcon
                  className={`w-4 h-4 ${isPush ? 'text-blue-600' : 'text-emerald-600'}`}
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-slate-900 truncate">
                    {suggestion.entityName}
                  </h3>
                  <span className="text-xs text-slate-400">
                    {formatRelativeTime(suggestion.timestamp)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className={`text-xs font-medium ${isPush ? 'text-blue-600' : 'text-emerald-600'}`}
                  >
                    {isPush ? 'Push' : 'Pull'}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span className="text-xs text-slate-500">
                    {changeCount} change{changeCount > 1 ? 's' : ''}
                  </span>
                  <span className="text-slate-300">·</span>
                  <span
                    className={`text-xs font-medium ${
                      suggestion.matchConfidence >= 90
                        ? 'text-emerald-600'
                        : suggestion.matchConfidence >= 75
                          ? 'text-amber-600'
                          : 'text-rose-600'
                    }`}
                  >
                    {suggestion.matchConfidence}%
                  </span>
                </div>
              </div>
            </div>

            {/* AI reasoning preview */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg max-w-xs">
              <Sparkles className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
              <p className="text-xs text-slate-600 truncate">
                {suggestion.aiReasoning}
              </p>
            </div>

            {/* Quick field preview */}
            <div className="hidden md:flex items-center gap-1">
              {suggestion.changes.slice(0, 2).map((change, i) => (
                <div
                  key={change.id}
                  className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600"
                >
                  {change.fieldLabel}
                </div>
              ))}
              {suggestion.changes.length > 2 && (
                <div className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-500">
                  +{suggestion.changes.length - 2}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onExpand(suggestion.id)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="View details"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onReject(suggestion.id)}
                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                title="Reject"
              >
                <X className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => onAccept(suggestion.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                  isPush
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                <Check className="w-3.5 h-3.5" />
                Sync
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Expanded detail modal/panel
const DetailPanel = ({
  suggestion,
  onClose,
  onAccept,
  onReject,
  onToggleField,
}: {
  suggestion: SyncSuggestion;
  onClose: () => void;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onToggleField: (suggestionId: string, fieldId: string) => void;
}) => {
  const isPush = suggestion.direction === 'push';
  const selectedCount = suggestion.changes.filter((c) => c.selected).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div
          className={`p-6 ${isPush ? 'bg-gradient-to-br from-blue-50 to-indigo-50' : 'bg-gradient-to-br from-emerald-50 to-teal-50'}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-xl ${isPush ? 'bg-blue-100' : 'bg-emerald-100'}`}
              >
                {isPush ? (
                  <ArrowUpRight className="w-6 h-6 text-blue-600" />
                ) : (
                  <ArrowDownLeft className="w-6 h-6 text-emerald-600" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {suggestion.entityName}
                </h2>
                <p className="text-sm text-slate-500 capitalize">
                  {suggestion.entityType} ·{' '}
                  {isPush ? 'Push to Filevine' : 'Pull from Filevine'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* AI Reasoning */}
          <div className="mt-4 p-3 bg-white/60 rounded-xl border border-white/80">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-violet-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-violet-600 mb-1">
                  AI Reasoning
                </p>
                <p className="text-sm text-slate-700">
                  {suggestion.aiReasoning}
                </p>
              </div>
            </div>
          </div>

          {/* Confidence */}
          <div className="mt-3 flex items-center gap-2">
            <div className="flex-1 h-2 bg-white/50 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  suggestion.matchConfidence >= 90
                    ? 'bg-emerald-500'
                    : suggestion.matchConfidence >= 75
                      ? 'bg-amber-500'
                      : 'bg-rose-500'
                }`}
                style={{ width: `${suggestion.matchConfidence}%` }}
              />
            </div>
            <span
              className={`text-sm font-semibold ${
                suggestion.matchConfidence >= 90
                  ? 'text-emerald-600'
                  : suggestion.matchConfidence >= 75
                    ? 'text-amber-600'
                    : 'text-rose-600'
              }`}
            >
              {suggestion.matchConfidence}% match
            </span>
          </div>
        </div>

        {/* Changes */}
        <div className="p-6 overflow-y-auto max-h-[40vh]">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Field Changes
          </h3>
          <div className="space-y-2">
            {suggestion.changes.map((change) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                key={change.id}
                className={`p-3 rounded-xl border transition-all cursor-pointer ${
                  change.selected
                    ? 'bg-white border-slate-200 shadow-sm'
                    : 'bg-slate-50 border-transparent'
                }`}
                onClick={() => onToggleField(suggestion.id, change.id)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      change.selected
                        ? isPush
                          ? 'bg-blue-500 border-blue-500'
                          : 'bg-emerald-500 border-emerald-500'
                        : 'bg-white border-slate-300'
                    }`}
                  >
                    {change.selected && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700">
                      {change.fieldLabel}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-xs ${change.selected ? 'text-slate-400 line-through' : 'text-slate-500'}`}
                      >
                        {change.currentValue || (
                          <span className="italic">Empty</span>
                        )}
                      </span>
                      <ArrowRight className="w-3 h-3 text-slate-300" />
                      <span
                        className={`text-xs font-medium ${change.selected ? (isPush ? 'text-blue-600' : 'text-emerald-600') : 'text-slate-500'}`}
                      >
                        {change.newValue || (
                          <span className="italic font-normal">Remove</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              onReject(suggestion.id);
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-rose-600 transition-colors"
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={() => {
              onAccept(suggestion.id);
              onClose();
            }}
            disabled={selectedCount === 0}
            className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all flex items-center gap-2 ${
              selectedCount > 0
                ? isPush
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/25'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/25'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <RefreshCw className="w-4 h-4" />
            Sync {selectedCount} Field{selectedCount !== 1 ? 's' : ''}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main compact view component
export const SyncSuggestionCardsCompact = () => {
  const [suggestions, setSuggestions] =
    useState<SyncSuggestion[]>(mockSuggestions);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'push' | 'pull'>(
    'all',
  );

  const filteredSuggestions = suggestions.filter((s) => {
    if (s.status !== 'pending') return false;
    if (activeFilter === 'all') return true;
    return s.direction === activeFilter;
  });

  const expandedSuggestion = suggestions.find((s) => s.id === expandedId);

  const handleAccept = (id: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: 'accepted' as const } : s,
      ),
    );
  };

  const handleReject = (id: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, status: 'rejected' as const } : s,
      ),
    );
  };

  const handleToggleField = (suggestionId: string, fieldId: string) => {
    setSuggestions((prev) =>
      prev.map((s) =>
        s.id === suggestionId
          ? {
              ...s,
              changes: s.changes.map((c) =>
                c.id === fieldId ? { ...c, selected: !c.selected } : c,
              ),
            }
          : s,
      ),
    );
  };

  const counts = {
    all: suggestions.filter((s) => s.status === 'pending').length,
    push: suggestions.filter(
      (s) => s.status === 'pending' && s.direction === 'push',
    ).length,
    pull: suggestions.filter(
      (s) => s.status === 'pending' && s.direction === 'pull',
    ).length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                <h1 className="text-lg font-semibold text-slate-900">
                  Sync Queue
                </h1>
              </div>
              <div className="flex items-center gap-1">
                {(['all', 'push', 'pull'] as const).map((filter) => (
                  <button
                    type="button"
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                      activeFilter === filter
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {filter === 'all'
                      ? 'All'
                      : filter === 'push'
                        ? 'Push'
                        : 'Pull'}
                    <span
                      className={`ml-1.5 ${activeFilter === filter ? 'text-slate-300' : 'text-slate-400'}`}
                    >
                      {counts[filter]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="space-y-2">
          <AnimatePresence mode="popLayout">
            {filteredSuggestions.map((suggestion, index) => (
              <CompactSyncCard
                key={suggestion.id}
                suggestion={suggestion}
                onAccept={handleAccept}
                onReject={handleReject}
                onExpand={setExpandedId}
                index={index}
              />
            ))}
          </AnimatePresence>

          {filteredSuggestions.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-3">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <p className="text-slate-600 font-medium">Queue empty</p>
              <p className="text-sm text-slate-400 mt-1">
                No pending suggestions
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      <AnimatePresence>
        {expandedSuggestion && (
          <DetailPanel
            suggestion={expandedSuggestion}
            onClose={() => setExpandedId(null)}
            onAccept={handleAccept}
            onReject={handleReject}
            onToggleField={handleToggleField}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SyncSuggestionCardsCompact;
